// Very lightweight markdown renderer — paragraphs, headings, lists, bold,
// inline code, and GFM tables. No external deps.
import React from "react";

function inline(text) {
  text = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  text = text.replace(/(^|\W)\*(?!\*)(.+?)\*(?!\*)/g, "$1<em>$2</em>");
  text = text.replace(/`([^`]+)`/g, "<code class='bg-slate-100 px-1 rounded text-[13px]'>$1</code>");
  return text;
}

/** Split a "| a | b | c |" markdown row into ["a","b","c"], trimming empties at edges. */
function splitTableRow(line) {
  const t = line.trim().replace(/^\||\|$/g, "");
  return t.split("|").map((s) => s.trim());
}

/** Detect if `sep` line is a table separator like "|---|:---:|---:|" */
function isTableSep(line) {
  return /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(line);
}

export default function Markdown({ children }) {
  const src = (children || "").toString();
  const lines = src.split(/\r?\n/);
  const blocks = [];
  let list = null;
  const flushList = () => {
    if (list) {
      blocks.push({ type: "ul", items: list });
      list = null;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trimEnd();

    // Detect table: header row (has "|") followed by separator row
    if (line.includes("|") && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      flushList();
      const header = splitTableRow(line);
      const align = splitTableRow(lines[i + 1]).map((c) => {
        const left = c.startsWith(":");
        const right = c.endsWith(":");
        if (left && right) return "center";
        if (right) return "right";
        return "left";
      });
      const rows = [];
      i += 2;
      while (i < lines.length && lines[i].includes("|") && lines[i].trim() !== "") {
        rows.push(splitTableRow(lines[i]));
        i++;
      }
      i--; // outer loop will i++
      blocks.push({ type: "table", header, align, rows });
      continue;
    }

    if (!line.trim()) { flushList(); continue; }

    const h = line.match(/^(#{1,4})\s+(.+)$/);
    if (h) {
      flushList();
      blocks.push({ type: "h", level: h[1].length, text: h[2] });
      continue;
    }
    const li = line.match(/^\s*[-*]\s+(.+)$/);
    if (li) {
      if (!list) list = [];
      list.push(li[1]);
      continue;
    }
    flushList();
    blocks.push({ type: "p", text: line });
  }
  flushList();

  return (
    <div className="space-y-2 leading-relaxed text-sm">
      {blocks.map((b, i) => {
        if (b.type === "h") {
          const Tag = `h${Math.min(6, b.level + 1)}`;
          return (
            <Tag key={i} className="font-outfit font-semibold text-slate-900 mt-3 mb-1 text-[15px]"
              dangerouslySetInnerHTML={{ __html: inline(b.text) }} />
          );
        }
        if (b.type === "ul") {
          return (
            <ul key={i} className="list-disc pl-5 space-y-1">
              {b.items.map((it, j) => (
                <li key={j} dangerouslySetInnerHTML={{ __html: inline(it) }} />
              ))}
            </ul>
          );
        }
        if (b.type === "table") {
          const align = b.align;
          return (
            <div key={i} className="overflow-x-auto my-2 rounded-lg border border-slate-200/70 bg-white/60">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200">
                    {b.header.map((h, j) => (
                      <th
                        key={j}
                        className="py-2 px-3 font-medium text-slate-700 text-[11px] uppercase tracking-wide"
                        style={{ textAlign: align[j] || "left" }}
                        dangerouslySetInnerHTML={{ __html: inline(h) }}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.rows.map((r, ri) => (
                    <tr key={ri} className="border-t border-slate-100 hover:bg-slate-50/60">
                      {r.map((c, ci) => (
                        <td
                          key={ci}
                          className="py-1.5 px-3 text-slate-800"
                          style={{ textAlign: align[ci] || "left" }}
                          dangerouslySetInnerHTML={{ __html: inline(c) }}
                        />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return <p key={i} dangerouslySetInnerHTML={{ __html: inline(b.text) }} />;
      })}
    </div>
  );
}
